import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import type {
  CreateCompanyDataResponse,
  CreateWorkspaceDataResponse,
  SignupAuthDataResponse,
  SignupUserResponse,
  SignupWorkspaceResponse,
} from '@core/api/workspace/models';

@Injectable({
  providedIn: 'root',
})
export class TokenStorageService {
  private static readonly ACCESS_TOKEN = 'workspace_access_token';
  private static readonly REFRESH_TOKEN = 'workspace_refresh_token';
  private static readonly EXPIRES_IN = 'workspace_expires_in';
  private static readonly LEGACY_ACCESS_TOKEN = 'access_token';
  private static readonly LEGACY_REFRESH_TOKEN = 'refresh_token';
  private static readonly LEGACY_EXPIRES_IN = 'expires_in';
  private static readonly USER = 'auth_user';
  private static readonly WORKSPACE = 'auth_workspace';
  private static readonly WORKSPACE_ACCESS_COUNT = 'workspace_access_count';
  private static readonly WORKSPACE_IDS = 'workspace_ids';

  private readonly platformId = inject(PLATFORM_ID);
  private readonly storage: Storage;

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      this.storage = localStorage;
      this.migrateLegacyKeys();
    } else {
      this.storage = {
        getItem: () => null,
        setItem: () => {},
        removeItem: () => {},
        clear: () => {},
        length: 0,
        key: () => null,
      };
    }
  }

  private migrateLegacyKeys(): void {
    const migrate = (from: string, to: string) => {
      const value = this.storage.getItem(from);
      if (value != null && this.storage.getItem(to) == null) {
        this.storage.setItem(to, value);
      }
      this.storage.removeItem(from);
    };
    migrate(
      TokenStorageService.LEGACY_ACCESS_TOKEN,
      TokenStorageService.ACCESS_TOKEN,
    );
    migrate(
      TokenStorageService.LEGACY_REFRESH_TOKEN,
      TokenStorageService.REFRESH_TOKEN,
    );
    migrate(
      TokenStorageService.LEGACY_EXPIRES_IN,
      TokenStorageService.EXPIRES_IN,
    );
  }

  setSession(data: SignupAuthDataResponse): void {
    this.setUserSession(data);

    if (data.workspace) {
      this.storage.setItem(TokenStorageService.WORKSPACE, JSON.stringify(data.workspace));
      this.clearWorkspaceAccessMetadata();
    }
  }

  setUserSession(data: SignupAuthDataResponse): void {
    this.storage.setItem(TokenStorageService.ACCESS_TOKEN, data.accessToken);
    this.storage.setItem(TokenStorageService.REFRESH_TOKEN, data.refreshToken);
    this.storage.setItem(TokenStorageService.EXPIRES_IN, data.expiresIn.toString());
    this.storage.setItem(TokenStorageService.USER, JSON.stringify(data.user));
    this.storage.removeItem(TokenStorageService.WORKSPACE);

    if (data.workspaceAccessCount !== undefined) {
      this.storage.setItem(
        TokenStorageService.WORKSPACE_ACCESS_COUNT,
        data.workspaceAccessCount.toString(),
      );
    } else {
      this.storage.removeItem(TokenStorageService.WORKSPACE_ACCESS_COUNT);
    }

    if (data.workspaceIds) {
      this.storage.setItem(
        TokenStorageService.WORKSPACE_IDS,
        JSON.stringify(data.workspaceIds),
      );
    } else {
      this.storage.removeItem(TokenStorageService.WORKSPACE_IDS);
    }
  }

  updateWorkspaceSession(
    data: SignupAuthDataResponse | CreateWorkspaceDataResponse | CreateCompanyDataResponse,
  ): void {
    this.storage.setItem(TokenStorageService.ACCESS_TOKEN, data.accessToken);
    this.storage.setItem(TokenStorageService.REFRESH_TOKEN, data.refreshToken);
    this.storage.setItem(TokenStorageService.EXPIRES_IN, data.expiresIn.toString());
    this.storage.setItem(TokenStorageService.WORKSPACE, JSON.stringify(data.workspace));
    this.clearWorkspaceAccessMetadata();
  }

  clearSession(): void {
    this.storage.removeItem(TokenStorageService.ACCESS_TOKEN);
    this.storage.removeItem(TokenStorageService.REFRESH_TOKEN);
    this.storage.removeItem(TokenStorageService.EXPIRES_IN);
    this.storage.removeItem(TokenStorageService.USER);
    this.storage.removeItem(TokenStorageService.WORKSPACE);
    this.clearWorkspaceAccessMetadata();
  }

  isAuthenticated(): boolean {
    return !!this.getAccessToken();
  }

  hasWorkspaceSession(): boolean {
    return !!this.getWorkspace();
  }

  getAccessToken(): string | null {
    return this.storage.getItem(TokenStorageService.ACCESS_TOKEN);
  }

  getRefreshToken(): string | null {
    return this.storage.getItem(TokenStorageService.REFRESH_TOKEN);
  }

  getUser(): SignupUserResponse | null {
    const raw = this.storage.getItem(TokenStorageService.USER);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as SignupUserResponse;
    } catch {
      return null;
    }
  }

  getWorkspace(): SignupWorkspaceResponse | null {
    const raw = this.storage.getItem(TokenStorageService.WORKSPACE);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as SignupWorkspaceResponse;
    } catch {
      return null;
    }
  }

  getWorkspaceAccessCount(): number {
    const raw = this.storage.getItem(TokenStorageService.WORKSPACE_ACCESS_COUNT);
    if (!raw) {
      return 0;
    }

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  getWorkspaceIds(): string[] {
    const raw = this.storage.getItem(TokenStorageService.WORKSPACE_IDS);
    if (!raw) {
      return [];
    }

    try {
      const parsed = JSON.parse(raw) as string[];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private clearWorkspaceAccessMetadata(): void {
    this.storage.removeItem(TokenStorageService.WORKSPACE_ACCESS_COUNT);
    this.storage.removeItem(TokenStorageService.WORKSPACE_IDS);
  }
}
